// Email one-time codes as a second factor.
//
// Identity Platform implements only PHONE and TOTP (its own `FactorId` enum
// has exactly those two), so the email factor is enforced by OUR backend at
// the point it mints the session cookie. Everything here talks to that
// backend rather than to Firebase.
import {
  currentAudience,
  invitationAccessHint,
  readAccess,
  saveAccess,
} from '../accessState';
import type { AccessSessionResponse } from '@biasafe.models';
import type { AccessAudience } from '../accessState';
import apiClient from '@/api';

/**
 * Send a code to the account's own address to begin enrolment.
 *
 * Returns how long the code stays valid, as reported by the server — the UI
 * must not hardcode it, or the promise on screen drifts from the TTL that
 * actually decides whether the code still works.
 */
export async function startEmailEnrollment(): Promise<number | null> {
  const { data } = await apiClient.post<{ codeTtlMinutes?: string }>(
    '/auth/mfa/email/enable',
  );
  const minutes = Number(data.codeTtlMinutes);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : null;
}

/** Confirm enrolment with the code that was emailed. */
export async function confirmEmailEnrollment(code: string): Promise<void> {
  await apiClient.post('/auth/mfa/email/confirm', { code: code.trim() });
}

/** Turn the email factor off. */
export async function disableEmailMfa(): Promise<void> {
  await apiClient.post('/auth/mfa/email/disable');
}

/**
 * Tell the backend the caller's second factors changed.
 *
 * Phone enrolment happens in the browser against Identity Platform, so the
 * backend is not in that exchange and learns nothing unless it is told. It does
 * not take our word for what happened: it re-reads the factors from the Admin
 * SDK and records those. Without this call there was no audit row for a phone
 * factor at all, and no notice to the account holder that one had been added.
 *
 * Best effort. The factor is already live at the provider by the time this
 * runs, so a failure here must not be reported as a failed enrolment.
 *
 * Returns the factor list the backend read from the Admin SDK, or null when
 * the call did not land. That list is the whole point of the endpoint — its
 * docstring says it "returns the authoritative factor list so the UI can
 * reconcile itself" — and this function used to declare `Promise<void>` and
 * throw the body away, so nothing ever reconciled against it. The caller was
 * left with the browser's own cached view, which is the copy that goes stale.
 */
export async function reportMfaChange(): Promise<Array<{
  uid: string;
  factorId: string;
  displayName: string | null;
}> | null> {
  try {
    const { data } = await apiClient.post<{
      factors?: Array<{
        uid?: string;
        factorId?: string;
        displayName?: string | null;
      }>;
    }>('/auth/mfa/enrolled');
    if (!Array.isArray(data.factors)) return null;
    return data.factors.map((f) => ({
      uid: f.uid ?? '',
      factorId: f.factorId ?? '',
      displayName: f.displayName ?? null,
    }));
  } catch {
    // Recorded server-side on the next call that reads the factor list.
    return null;
  }
}

/**
 * Finish a sign-in that `/auth/gcp/session` held back for an email code.
 *
 * Re-sends the ID token because this endpoint is what mints the cookie — it
 * verifies the token again rather than trusting that the earlier call
 * happened, or happened for this user.
 */
export async function completeEmailChallenge(
  idToken: string,
  code: string,
  audience: AccessAudience = currentAudience(),
): Promise<AccessSessionResponse> {
  const { data } = await apiClient.post<AccessSessionResponse>(
    '/auth/mfa/email/challenge',
    {
      id_token: idToken,
      code: code.trim(),
      audience,
      access_id: invitationAccessHint() ?? readAccess(audience)?.choice.id,
    },
  );
  saveAccess(data, audience);
  return data;
}

/**
 * Send a NEW sign-in code to someone held at the email challenge.
 *
 * Not `/auth/gcp/session`: that is the automatic trigger and returns the
 * outstanding code for two minutes without sending anything, so "Resend" was a
 * dead button for the second minute of its own cooldown.
 */
export async function resendEmailChallenge(idToken: string): Promise<void> {
  await apiClient.post('/auth/mfa/email/challenge/resend', {
    id_token: idToken,
  });
}

/**
 * What a failed email-code call should tell the person, or null when the error
 * is not one we can describe (the caller then uses its own fallback).
 *
 * The backend answers 400 for a wrong code, an expired one and one whose
 * attempts ran out, and says which in `detail.data.reason`. Telling someone to
 * "check the digits" when the code had expired sent them round the same loop
 * until they thought the factor was broken.
 */
export function emailMfaFailureMessage(err: unknown): string | null {
  if (typeof err !== 'object' || err === null || !('response' in err)) {
    return null;
  }
  const res = (
    err as {
      response?: {
        status?: number;
        data?: { detail?: { data?: { reason?: string } } };
      };
    }
  ).response;
  if (res?.status === 429) {
    return 'Too many attempts. Wait a few minutes and try again.';
  }
  if (res?.status === 502) {
    return 'We could not send the email just now. Try again in a moment.';
  }
  switch (res?.data?.detail?.data?.reason) {
    case 'no_code':
      return 'That code has expired. Send a new one and enter it right away.';
    case 'too_many_attempts':
      return 'Too many wrong codes. Send a new code and try again.';
    case 'wrong':
      return 'That code is not right. Check it and try again.';
    case 'last_factor':
      return 'This is your only sign-in method. Add another before removing it.';
    default:
      return null;
  }
}

/**
 * Whether this error is the backend saying "email code required".
 *
 * The API error shape is `{detail: {code, message, data}}`; the marker lives
 * at `detail.data.mfaRequired`. Narrow rather than assume — a 401 from this
 * endpoint could equally mean the token was rejected, and treating that as an
 * MFA prompt would strand the user on a code screen they can never satisfy.
 */
export function isEmailMfaRequired(err: unknown): boolean {
  // Guarded rather than optional-chained: a network failure or a plain Error
  // reaches the same catch block, and reading `.response` off undefined would
  // throw INSIDE the error handler — masking the real failure with a
  // TypeError.
  if (typeof err !== 'object' || err === null || !('response' in err)) {
    return false;
  }
  const data = (
    err as {
      response?: { status?: number; data?: { detail?: { data?: unknown } } };
    }
  ).response;
  if (data?.status !== 401) return false;
  const marker = data.data?.detail?.data as
    { mfaRequired?: string } | undefined;
  return marker?.mfaRequired === 'email';
}

export interface MfaStatus {
  enrolled: boolean;
  methods: Array<string>;
  /** True when the provider could not be reached — see the backend route. */
  degraded: boolean;
  /**
   * When an unenrolled user must enrol (MFA_ENFORCEMENT_MODE). Absent on an
   * older API, which is `before_onboarding`.
   */
  enforcementMode?: MfaEnforcementMode;
}

export type MfaEnforcementMode =
  'before_onboarding' | 'before_regulated_action';

/**
 * Whether the user may use the app unenrolled until their first regulated
 * action. Anything but the explicit new mode is the old, strict behaviour, so an
 * unknown or missing value can never loosen the gate.
 */
export function mfaIsDeferred(
  status: Pick<MfaStatus, 'enforcementMode'>,
): boolean {
  return status.enforcementMode === 'before_regulated_action';
}

/**
 * Whether this account has a second factor. Authoritative — read by the
 * backend from Identity Platform and the Firebase custom claim, never from
 * anything the browser can influence.
 *
 * A client that could sway this answer could opt itself out of a regulatory
 * control, so the router gate must call THIS rather than inspect local state.
 */
export async function fetchMfaStatus(): Promise<MfaStatus> {
  const { data } = await apiClient.get<MfaStatus>('/auth/mfa/status');
  return data;
}

/**
 * How long the code the backend just emailed stays valid, per the server.
 *
 * Read from the same `mfaRequired` payload rather than written into the sign-in
 * screen as a literal. The screen used to promise "10 minutes" while the server
 * expired the code after 30 and the email said 30 — the one number the user
 * actually plans around was the only one that was wrong. Returns null when the
 * field is absent so the copy can drop the sentence instead of inventing one.
 */
export function emailMfaTtlMinutes(err: unknown): number | null {
  if (typeof err !== 'object' || err === null || !('response' in err)) {
    return null;
  }
  const res = (err as { response?: { data?: { detail?: { data?: unknown } } } })
    .response;
  const marker = res?.data?.detail?.data as
    { codeTtlMinutes?: number | string } | undefined;
  const minutes = Number(marker?.codeTtlMinutes);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : null;
}
